'use client';

import Image from 'next/image';
import { ExternalLink, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface TradeButtonProps {
  /** The token/coin contract address */
  coinAddress: string;
  /** Optional coin symbol for display */
  coinSymbol?: string;
  /** Button size variant */
  size?: 'default' | 'sm' | 'lg' | 'icon';
  /** Additional className */
  className?: string;
}

interface TradingPlatform {
  name: string;
  label: string;
  getUrl: (address: string) => string;
  icon: string;
}

// Get network slug for URLs based on chain ID
const chainId = process.env.NEXT_PUBLIC_CHAIN_ID;
const flaunchNetwork = chainId === '8453' ? 'base' : 'base-sepolia';

const TRADING_PLATFORMS: TradingPlatform[] = [
  {
    name: 'flaunch',
    label: 'Flaunch',
    getUrl: (address) => `https://flaunch.gg/${flaunchNetwork}/coin/${address}`,
    icon: '/icons/flaunch.ico',
  },
  {
    name: 'uniswap',
    label: 'Uniswap',
    getUrl: (address) => `https://app.uniswap.org/swap?chain=base&outputCurrency=${address}`,
    icon: '/icons/uniswap.ico',
  },
  {
    name: 'dexscreener',
    label: 'Dexscreener',
    getUrl: (address) => `https://dexscreener.com/base/${address}`,
    icon: '/icons/dexscreener.ico',
  },
];

export function TradeButton({
  coinAddress,
  coinSymbol,
  size = 'sm',
  className,
}: TradeButtonProps) {
  const handlePlatformClick = (platform: TradingPlatform) => {
    const url = platform.getUrl(coinAddress);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size={size} className={className}>
          Trade
          <ChevronDown className="ml-1 h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {TRADING_PLATFORMS.map((platform) => (
          <DropdownMenuItem
            key={platform.name}
            onClick={() => handlePlatformClick(platform)}
            className="cursor-pointer"
          >
            <Image
              src={platform.icon}
              alt={platform.label}
              width={16}
              height={16}
              className="shrink-0"
            />
            <span className="flex-1">{platform.label}</span>
            <ExternalLink className="h-3 w-3 text-muted-foreground" />
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
